import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import Link from '@atlaskit/link/link';
import { Text } from '@atlaskit/primitives/compiled/text';

const RUNBOOK_URL =
	'https://hello.atlassian.net/wiki/spaces/~7120202da6a0b2c4184c0da3ba8ade6d08feaa/pages/5761728624/React+suspense+boundary+errors+run-book';

type QueryBoundaryErrorMessageProps = {
	operationName: string;
	errorType: 'outside-boundary' | 'boundary-mismatch';
	args?: {
		expectedBoundaries?: string[];
		currentBoundaries?: string[];
		boundaryName?: string;
	};
};

const i18n = defineMessages({
	outsideBoundaryError: {
		id: 'streaming-boundary-error-notification.outside-boundary.message',
		defaultMessage:
			// oxlint-disable-next-line eslint-core/no-restricted-syntax
			'Query "{operationName}" preloaded in [{expectedBoundaries}] but accessed outside any suspense boundary.',
		description: 'Error message when a query is accessed outside any suspense boundary.',
	},
	boundaryMismatchError: {
		id: 'streaming-boundary-error-notification.boundary-mismatch.message',
		defaultMessage:
			// oxlint-disable-next-line eslint-core/no-restricted-syntax
			'Query "{operationName}" preloaded in [{expectedBoundaries}] but accessed in "{currentBoundaries}" boundary.',
		description: 'Error message when a query is accessed in a mismatched suspense boundary.',
	},
	runbookLink: {
		id: 'streaming-boundary-error-notification.runbook.link',
		defaultMessage: 'See <runbookLink>this page</runbookLink> for details.',
		description: 'Link to the runbook page for more details.',
	},
});

export const QueryBoundaryErrorMessage: React.FC<QueryBoundaryErrorMessageProps> = ({
	operationName,
	errorType,
	args,
}: QueryBoundaryErrorMessageProps) => {
	const expectedBoundaries = args?.expectedBoundaries?.join(', ') || '';
	const currentBoundaries = args?.currentBoundaries?.join(' → ') || '';

	const renderMessage = () => {
		switch (errorType) {
			case 'outside-boundary':
				return (
					<FormattedMessage
						{...i18n.outsideBoundaryError}
						values={{
							operationName,
							expectedBoundaries,
						}}
					/>
				);
			case 'boundary-mismatch':
				return (
					<FormattedMessage
						{...i18n.boundaryMismatchError}
						values={{
							operationName,
							expectedBoundaries,
							currentBoundaries,
						}}
					/>
				);
		}
	};

	return (
		<div data-testid="queryBoundaryErrorMessage">
			<Text>{renderMessage()}</Text>
			{(errorType === 'boundary-mismatch' || errorType === 'outside-boundary') && (
				<FormattedMessage
					{...i18n.runbookLink}
					values={{
						runbookLink: (chunks: React.ReactNode[]) => (
							<Link target="_blank" href={RUNBOOK_URL}>
								{chunks}
							</Link>
						),
					}}
				/>
			)}
		</div>
	);
};
